import { ContractStatus, ContractTermUnit } from '../../../types/compliance';

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * End Date implied by a Contract Length: the day before the same calendar
 * date `length` months/years after `startDate` (1 year from 2026-01-01 ends
 * 2026-12-31). Month-end starts clamp rather than spill into the next month.
 * Works on YYYY-MM-DD strings in UTC so the user's time zone can't shift it.
 */
export function endDateForTerm(
  startDate: string,
  length: number,
  unit: ContractTermUnit,
): string | null {
  const m = startDate.match(ISO_DATE);
  if (!m || !Number.isInteger(length) || length < 1) return null;
  const [year, month, day] = [Number(m[1]), Number(m[2]) - 1, Number(m[3])];
  const totalMonths = month + (unit === 'years' ? length * 12 : length);
  const targetYear = year + Math.floor(totalMonths / 12);
  const targetMonth = totalMonths % 12;
  const lastDay = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
  const anniversary = Date.UTC(targetYear, targetMonth, Math.min(day, lastDay));
  return new Date(anniversary - 86_400_000).toISOString().slice(0, 10);
}

/** "1 year", "18 months"; '' when no length is set. */
export function formatTermLength(
  length: number | null,
  unit: ContractTermUnit | null,
): string {
  if (!length || !unit) return '';
  const singular = unit === 'years' ? 'year' : 'month';
  return `${length} ${length === 1 ? singular : `${singular}s`}`;
}

interface LengthFields {
  term_length: number | null;
  term_unit: ContractTermUnit | null;
}

interface TermFields extends LengthFields {
  auto_renewal: boolean;
}

/**
 * Contract Length of 1 month, whether or not it auto-renews. Shown gray with a
 * "Monthly" badge in the list and left out of the dashboard's "expiring"
 * numbers. Mirrors `is_monthly` in backend/app/models/compliance/contract.py.
 */
export function isMonthly(c: LengthFields): boolean {
  return c.term_length === 1 && c.term_unit === 'months';
}

/**
 * 1-month length + auto-renewal: the contract just rolls over monthly. It gets
 * no reminders and is never Pending Renewal. Mirrors `is_month_to_month` in
 * backend/app/models/compliance/contract.py.
 */
export function isMonthToMonth(c: TermFields): boolean {
  return c.auto_renewal && isMonthly(c);
}

/** Status as shown: a month-to-month Pending Renewal reads as Active. */
export function effectiveStatus(c: TermFields & { status: ContractStatus }): ContractStatus {
  return c.status === 'pending_renewal' && isMonthToMonth(c) ? 'active' : c.status;
}

/**
 * Monthly Cost suggested from Total Contract Value ÷ Contract Length in
 * months, as a 2-decimal string ('' when either is missing or invalid).
 */
export function suggestedMonthlyCost(
  total: string,
  termLength: string,
  unit: ContractTermUnit,
): string {
  const value = Number(total);
  const length = Number(termLength);
  if (total.trim() === '' || !Number.isFinite(value) || value < 0) return '';
  if (termLength.trim() === '' || !Number.isInteger(length) || length < 1) return '';
  const months = unit === 'years' ? length * 12 : length;
  return (value / months).toFixed(2);
}

interface MonthlyCostFields {
  contract_value: string;
  monthly_cost: string;
  term_length: string;
  term_unit: ContractTermUnit;
}

/**
 * Re-applies the Monthly Cost suggestion after a Total / Length edit, unless
 * the user typed their own figure (the old value isn't blank and isn't the
 * old suggestion).
 */
export function withMonthlyCostSuggestion<T extends MonthlyCostFields>(prev: T, next: T): T {
  const prevSuggestion = suggestedMonthlyCost(prev.contract_value, prev.term_length, prev.term_unit);
  if (prev.monthly_cost !== '' && prev.monthly_cost !== prevSuggestion) return next;
  return {
    ...next,
    monthly_cost: suggestedMonthlyCost(next.contract_value, next.term_length, next.term_unit),
  };
}
